import * as Sentry from "@sentry/nextjs";
import type { Breadcrumb, BreadcrumbHint, ErrorEvent } from "@sentry/nextjs";

const retryKey = "atlas.application-recovery";
const retryLifetimeMs = 5 * 60 * 1000;
let observationId: string | undefined;

function isApplicationPath(path: string) {
  return /^\/(?:(?:a\/[^/]+|[^/]+)\/)?applications\/(?:APP-|APPLICATION-)?\d+\/?$/i.test(path);
}

export function observeApplicationRecovery(
  phase: "navigation_started" | "fallback_displayed" | "retry_initiated" | "application_committed",
  destination?: string,
) {
  try {
    if (typeof window === "undefined") return;
    const target = new URL(destination ?? window.location.href, window.location.origin);
    if (target.origin !== window.location.origin || !isApplicationPath(target.pathname)) return;
    if (process.env.NEXT_PUBLIC_ATLAS_E2E_DISABLE_EXTERNAL_TELEMETRY === "1") return;
    let followsRetry = false;
    if (phase === "application_committed" || phase === "fallback_displayed") {
      let saved: string | null = null;
      try { saved = window.sessionStorage.getItem(retryKey); } catch {}
      if (saved) {
        window.sessionStorage.removeItem(retryKey);
        const pending: unknown = JSON.parse(saved);
        if (pending && typeof pending === "object" && "id" in pending && "at" in pending
          && typeof pending.id === "string" && /^[a-f0-9-]{36}$/.test(pending.id)
          && typeof pending.at === "number" && Date.now() >= pending.at
          && Date.now() - pending.at < retryLifetimeMs) {
          observationId = pending.id;
          followsRetry = true;
        }
      }
    }
    observationId ??= crypto.randomUUID();
    if (phase === "retry_initiated") {
      try { window.sessionStorage.setItem(retryKey, JSON.stringify({ id: observationId, at: Date.now() })); } catch {}
    }
    const data = { observationId, phase, followsRetry, correlationScope: "same_tab", bodyCompletion: "unknown" };
    Sentry.addBreadcrumb({ category: "atlas.application.recovery", level: "info", data });
    if (phase === "fallback_displayed" || phase === "retry_initiated" || followsRetry) {
      Sentry.captureMessage("Application recovery observation", {
        level: "info",
        fingerprint: ["atlas.application.recovery"],
        contexts: { "atlas.application.recovery": data },
      });
    }
  } catch {}
}

export function observeApplicationRequest(breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb {
  try {
    if (typeof window === "undefined" || breadcrumb.category !== "fetch") return breadcrumb;
    const url = breadcrumb.data?.url;
    if (typeof url !== "string") return breadcrumb;
    const request = new URL(url, window.location.origin);
    if (request.origin !== window.location.origin || !isApplicationPath(request.pathname)
      || !request.searchParams.has("_rsc")) return breadcrumb;
    observationId ??= crypto.randomUUID();
    const error = hint?.data;
    const outcome = typeof breadcrumb.data?.status_code === "number"
      ? "response_headers"
      : error instanceof Error && error.name === "AbortError"
        ? "request_aborted"
        : error instanceof Error ? "request_rejected" : "unknown";
    return {
      ...breadcrumb,
      data: {
        url: "/applications/[application-id]",
        method: breadcrumb.data?.method === "GET" ? "GET" : "other",
        ...(typeof breadcrumb.data?.status_code === "number" ? { status_code: breadcrumb.data.status_code } : {}),
        "atlas.application.observation_id": observationId,
        "atlas.application.request_outcome": outcome,
        "atlas.application.body_completion": "unknown",
      },
    };
  } catch {
    return breadcrumb;
  }
}

export function sanitizeApplicationObservation(event: ErrorEvent): ErrorEvent {
  if (event.message !== "Application recovery observation") return event;
  return {
    type: undefined,
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: event.platform,
    environment: event.environment,
    release: event.release,
    message: event.message,
    level: "info",
    fingerprint: ["atlas.application.recovery"],
    contexts: { "atlas.application.recovery": event.contexts?.["atlas.application.recovery"] },
  };
}
